import { useSelectedLanguageName } from '@/hooks/useSelectedLanguageName'
import ContinueButton from '@app-studio-web/ui/components/ContinueButton'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'
import QuizLayout from './QuizLayout'

const TITLE = 'pages.quizOnboarding.learnerProfile.title'

const LEVEL_KEYS: Record<string, string> = {
  beginner: 'pages.quizOnboarding.learnerProfile.levels.beginner',
  elementary: 'pages.quizOnboarding.learnerProfile.levels.elementary',
  intermediate: 'pages.quizOnboarding.learnerProfile.levels.intermediate',
  advanced: 'pages.quizOnboarding.learnerProfile.levels.advanced',
  confident: 'pages.quizOnboarding.learnerProfile.levels.confident',
}

const MOTIVATION_KEYS: Record<string, string> = {
  travel: 'pages.quizOnboarding.motivation.answer1',
  job: 'pages.quizOnboarding.motivation.answer2',
  family: 'pages.quizOnboarding.motivation.answer3',
  confidence: 'pages.quizOnboarding.motivation.answer4',
  brain: 'pages.quizOnboarding.motivation.answer5',
  culture: 'pages.quizOnboarding.motivation.answer6',
}

const DAILY_GOAL_KEYS: Record<string, string> = {
  '3_min': 'pages.quizOnboarding.dailyGoal.answer1',
  '5_min': 'pages.quizOnboarding.dailyGoal.answer2',
  '10_min': 'pages.quizOnboarding.dailyGoal.answer3',
  '15_min': 'pages.quizOnboarding.dailyGoal.answer4',
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-[#f1f5f9] py-3 last:border-b-0">
      <span className="text-sm text-[#94a3b8]">{label}</span>
      <span className="text-custom-black text-right text-sm font-bold">
        {value}
      </span>
    </div>
  )
}

export default function LearnerProfilePage() {
  const { t } = useTranslation()
  const language = useSelectedLanguageName()

  const comfortLevel = localStorage.getItem('comfortLevel') ?? 'beginner'
  const motivation = localStorage.getItem('quizMotivation') ?? ''
  const dailyGoal = localStorage.getItem('quizDailyGoal') ?? ''
  const nervousness = localStorage.getItem('quizNervousness') ?? 'yes'

  const levelLabel = t(LEVEL_KEYS[comfortLevel] ?? LEVEL_KEYS.beginner)
  const goalLabel = MOTIVATION_KEYS[motivation]
    ? t(MOTIVATION_KEYS[motivation])
    : t('pages.quizOnboarding.learnerProfile.goalDefault')
  const commitmentLabel = DAILY_GOAL_KEYS[dailyGoal]
    ? t(DAILY_GOAL_KEYS[dailyGoal])
    : t('pages.quizOnboarding.learnerProfile.commitmentDefault')
  const confidenceLabel =
    nervousness === 'not_really'
      ? t('pages.quizOnboarding.learnerProfile.confidentTag')
      : t('pages.quizOnboarding.learnerProfile.nervousTag')

  return (
    <QuizLayout>
      <div className="flex grow flex-col justify-center gap-6">
        <h1 className="text-custom-black text-center text-2xl font-extrabold text-balance">
          {t(TITLE, { language })}
        </h1>

        <div className="mx-auto w-full max-w-md rounded-3xl border border-[#e2e8f0] bg-white p-6 shadow-sm">
          <div className="mb-4 flex justify-center">
            <span className="bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-semibold">
              {confidenceLabel}
            </span>
          </div>

          <ProfileRow
            label={t('pages.quizOnboarding.learnerProfile.languageLabel')}
            value={language}
          />
          <ProfileRow
            label={t('pages.quizOnboarding.learnerProfile.levelLabel')}
            value={levelLabel}
          />
          <ProfileRow
            label={t('pages.quizOnboarding.learnerProfile.goalLabel')}
            value={`🎯 ${goalLabel}`}
          />
          <ProfileRow
            label={t('pages.quizOnboarding.learnerProfile.commitmentLabel')}
            value={commitmentLabel}
          />
        </div>
      </div>

      <Form className="shrink-0" method="post">
        <input
          name={t(TITLE, { lng: 'en', language })}
          type="hidden"
          value={t('shared.continue', { lng: 'en' })}
        />

        <ContinueButton />
      </Form>
    </QuizLayout>
  )
}
